/* eslint-disable prettier/prettier */
import { ChangeEvent, FC, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogProps,
  Stack,
  Typography,
  Checkbox,
  FormControlLabel,
  Box,
  useMediaQuery,
  Theme,
} from '@mui/material';
import { CheckboxCheckedIcon, CheckboxIcon } from 'assets';

export interface AINotesTermsAndConditionsConfirmationDialogProps extends DialogProps {
  onConfirm(): void;
  onCancel(): void;
}

export const AINotesTermsAndConditionsConfirmationDialog: FC<AINotesTermsAndConditionsConfirmationDialogProps> = ({
  onConfirm,
  onCancel,
  ...props
}) => {
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const [isChecked, setIsChecked] = useState<boolean>(false);

  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setIsChecked(event.target.checked);
  };

  const handleConfirm = async (): Promise<void> => {
    onConfirm?.();
  };

  return (
    <Dialog
      fullScreen={isMobile}
      {...props}
      sx={{
        zIndex: 9999,
        '.MuiPaper-root': {
          maxWidth: '1100px',
          width: '100%',
        },
      }}
    >
      <DialogContent
        sx={({ breakpoints }) => ({
          padding: '37px 29px 27px 46px',
          maxWidth: '100%',
          width: '100%',
          [breakpoints.down(599)]: {
            display: 'grid',
            gridTemplateColumns: '1fr',
            gridTemplateRows: '.6fr auto .6fr',
            padding: '37px 29px 27px 36px',
          },
          [breakpoints.down(391)]: {
            gridTemplateRows: '.4fr auto .3fr',
            paddingBlock: '27px',
            paddingInlineStart: '16px',
            paddingInlineEnd: '14px',
          },
        })}
      >
        <Stack
          direction='column'
          justifyContent='space-between'
          gap='8px'
          sx={({ breakpoints }) => ({
            [breakpoints.down(599)]: {
              gridColumnStart: 1,
              gridColumnEnd: 2,
              gridRowStart: 1,
              gridRowEnd: 2,
            },
          })}
        >
          <Typography
            sx={{
              color: '#81CFDD',
              fontSize: '24px',
              fontWeight: 700,
              lineHeight: '36px',
              textAlign: 'left',
            }}
          >
            Terms of Service
          </Typography>
        </Stack>
        <Box
          sx={({ breakpoints }) => ({
            height: 408,
            overflow: 'auto',
            marginBlockStart: '18px',
            marginBlockEnd: '36px',
            paddingRight: '46px',
            [breakpoints.down(768)]: {
              paddingRight: '18px',
              marginBlockEnd: '18px',
            },
            [breakpoints.down(599)]: {
              gridColumnStart: 1,
              gridColumnEnd: 2,
              gridRowStart: 2,
              gridRowEnd: 3,
              height: 'calc(100vh - 286px)',
            },
          })}
        >
          <Typography
            component='p'
            sx={{
              color: '#979797',
              fontSize: '16px',
              fontWeight: 500,
              lineHeight: '25.5px',
              textAlign: 'justify',
              a: {
                color: 'blue',
                textDecoration: 'underline',
                margin: 0,
              },
            }}
          >
            <>
              <p>
                <strong>Updated February 04, 2025</strong>
              </p>
              <p>
                [See also{' '}
                <a href='https://www.hipaalink.net/privacy-policy/' target='_blank'>
                  Privacy Policy
                </a>
                ]
              </p>
              <p>
                Welcome, and thank you for your interest in HIPAALINK.net, Inc. (“HIPAA LINK,” “we,” or “us”) and our AI
                Progress Notes tool, the “Service”). These Terms of Service are a legally binding contract between you
                and HIPAA LINK regarding your use of the Service. As used herein, “you” or “your” means any entity,
                university, organization, or company accessing or using the Service (“Organization”) as well as any
                individual end user accessing and using the Service, as applicable and hereby agreeing to these Terms.
              </p>
              <p>
                <strong>PLEASE READ THE FOLLOWING TERMS CAREFULLY.</strong>
              </p>
              <p>
                <strong>
                  BY CLICKING “I ACCEPT,” OR BY DOWNLOADING, INSTALLING, OR OTHERWISE ACCESSING OR USING THE SERVICE
                </strong>
                , YOU AGREE THAT YOU HAVE READ AND UNDERSTOOD, AND, AS A CONDITION TO YOUR USE OF THE SERVICE, YOU AGREE
                TO BE BOUND BY, THE FOLLOWING TERMS AND CONDITIONS, INCLUDING HIPAA LINK’S{' '}
                <a href='https://www.hipaalink.net/privacy-policy/' target='_blank'>
                  PRIVACY POLICY
                </a>{' '}
                (TOGETHER, THESE “<strong>TERMS</strong>”). IF YOU ARE NOT ELIGIBLE, OR DO NOT AGREE TO THE TERMS, THEN
                YOU DO NOT HAVE OUR PERMISSION TO USE THE SERVICE. YOUR USE OF THE SERVICE, AND HIPAA LINK’S PROVISION
                OF THE SERVICE TO YOU, CONSTITUTES AN AGREEMENT BY HIPAA LINK AND BY YOU TO BE BOUND BY THESE TERMS.
              </p>
              <p>
                <strong>HIPAA Compliance Statement</strong> HIPAA LINK acknowledges its status as a Business Associate
                under the Health Insurance Portability and Accountability Act (HIPAA). HIPAA LINK shall comply with all
                applicable HIPAA regulations and provide Business Associate Agreements (BAAs) to Covered Entities and
                Business Associates using the AI Progress Notes service. Customers are responsible for ensuring that
                they obtain and execute a BAA with HIPAA LINK before using the service in connection with Protected
                Health Information (PHI).
              </p>
              <h2>Privacy Law Compliance Update</h2>
              <p>
                HIPAA LINK’s data processing practices comply with the General Data Protection Regulation (GDPR), the
                United Kingdom General Data Protection Regulation (UK GDPR), and U.S. state privacy laws, including
                the California Privacy Rights Act (CPRA), Colorado Privacy Act (ColoPA), Virginia Consumer Data Protection
                Act (VCDPA), Connecticut Personal Data Privacy and Online Monitoring Act (CPOMA), Utah Consumer Privacy
                Act (UCPA), and the Texas Data Privacy and Security Act (effective July 2024). HIPAA LINK shall notify
                customers of any material changes in its privacy policies that affect data processing obligations.
              </p>
              <h2>Service Description and User Responsibilities</h2>
              <p>
                The Service enables users to transform recorded voice conversations into transcribed notes. Users may
                either use the free credits provided by HIPAA LINK (“Free Credits”) or purchase prepaid service credits
                (“Prepaid Service Credits”). Users must ensure that all recorded conversations comply with applicable
                laws, including obtaining necessary consent from participants.
              </p>
              <h2>Prohibited Conduct</h2>
              <p>Users may not:</p>
              <ul>
                <li>
                  Use the Service for any illegal purpose or in violation of any laws.
                </li>
                <li>
                  Upload or distribute harmful or unauthorized content.
                </li>
                <li>
                  Misrepresent their identity or create unauthorized accounts.
                </li>
                <li>
                  Interfere with the security or operation of the Service.
                </li>
                <li>
                  Attempt to reverse engineer, decompile, modify, or manipulate AI functionalities within the platform.
                </li>
                <li>
                  Use AI-generated transcriptions to create deceptive, misleading, or fraudulent content.
                </li>
              </ul>
              <h2>Recording and Data Retention Policy</h2>
              <p>
                Users are responsible for ensuring compliance with applicable recording laws. The Service may store
                transcriptions and recordings for <strong>up to 30 days</strong>, subject to user preferences and legal requirements.
                Customers may delete stored content, and once deleted, it cannot be recovered.
              </p>
              <h2>Arbitration Clause & Dispute Resolution</h2>
              <p>
                Users may opt out of arbitration by submitting a written request <strong>or completing an opt-out form available
                on the HIPAA LINK website</strong> within 30 days of accepting these Terms. If a user opts out, disputes will be
                resolved in accordance with the governing law clause. Arbitration proceedings will take place in
                Pennsylvania.
              </p>
              <h2>Indemnification</h2>
              <p>
                Users agree to indemnify and hold harmless HIPAA LINK from any claims, damages, liabilities, costs, and
                expenses arising from their misuse of the Service, violation of these Terms, infringement of any third-party
                rights, <strong>or any claims related to inaccuracies, biases, or errors generated by the AI functionalities within
                the platform, including transcription services. Users further agree to indemnify HIPAA LINK against any claims
                resulting from decisions made or actions taken based on AI-generated transcriptions or outputs.</strong>
              </p>
              <p>
                <strong>HIPAA LINK does not assume liability for third-party claims related to AI inaccuracies, even if users rely
                on AI-generated transcriptions for professional or regulatory purposes. Users acknowledge that AI-based
                content may not be error-free and accept full responsibility for reviewing and verifying all AI-generated
                outputs before use.</strong>
              </p>
              <h2>User Content Ownership & AI Data Use Disclosure</h2>
              <p>
                HIPAA LINK affirms that all User Content remains the sole property of the customer. HIPAA LINK does not
                claim ownership of any user-generated AI-enhanced content. <strong>User Content will not be used for training
                third-party AI models</strong>. Aggregated, de-identified data may be used solely for internal improvements and
                analytics.
              <p>
                Any AI-generated content produced through the Service is subject to the same ownership terms as
                user-generated content unless otherwise specified in a separate agreement. Customers may request deletion
                of their data at any time, subject to retention policies required by law.
              </p>
              <h2>Payment and Refund Policy</h2>
              <p>
                HIPAA LINK may offer prepaid service credits that users can purchase for access to the Service. <strong>Prepaid
                service credits are refundable on a prorated basis if a user terminates their account within 30 days of
                purchase</strong>. Refund requests must be submitted in writing to HIPAA LINK support. Refunds will be processed
                within 30 business days. <strong>Promotional credits and free credits are non-refundable</strong>. Service credits are
                non-transferable and may not be redeemed for cash.
              </p>
              <h2>Limitation of Liability</h2>
              <p>
                The total liability of HIPAA LINK shall not exceed the greater of: (a) three times the amount paid by the
                user in the 12 months preceding the claim, or (b) $500, except where prohibited by law. <strong>HIPAA LINK disclaims
                all warranties regarding service reliability, performance, and uninterrupted operation</strong>.
              </p>
              <p>
                HIPAA LINK shall not be liable for any errors, misinterpretations, or inaccuracies arising from the use
                of AI-powered transcription or other AI-generated outputs. Users acknowledge that AI-based processing may
                not be error-free and assume full responsibility for verifying the accuracy and completeness of AI-generated
                transcriptions and content before relying on them. <strong>Furthermore, HIPAA LINK is not responsible for any
                regulatory compliance obligations users may have concerning their reliance on AI-generated transcriptions
                or other AI-driven outputs. Users bear full responsibility for ensuring compliance with applicable industry
                regulations and professional standards</strong>.
              </p>
              <h2>Termination and Suspension</h2>
              <p>
                HIPAA LINK reserves the right to suspend or terminate access to the Service at its discretion, with or
                without notice, in cases of non-compliance with these Terms or applicable laws.
              </p>
              <h2>Modification of Terms</h2>
              <p>
                HIPAA LINK may modify these Terms at any time. Users will be notified of material changes, including
                modifications to AI-related functionalities, via email or through a notice on the Service at least
                30 days before such changes take effect. Users are responsible for reviewing updates, and continued
                use of the Service constitutes acceptance of the revised Terms. Any changes related to AI functionalities
                will be implemented in compliance with evolving AI regulations and industry best practices.
              </p>
              <p>
                If a user does not agree with a material modification to AI-related functionalities, they may terminate
                their agreement within 30 days of receiving notice of the change without penalty by providing written
                notice to HIPAA LINK at support@hipaalink.net.
              </p>
              <h2>DMCA Notice</h2>
              <p>
              If you believe content on the Service infringes your copyright, you may submit a DMCA takedown notice
              to HIPAA LINK at contact@hipaalink.net.
              </p>
              <h2>Governing Law & Jurisdiction</h2>
              <p>
                These Terms are governed by and construed under the laws of the State of Pennsylvania, without regard
                to its conflict of laws principles. Any disputes arising under these Terms shall be resolved exclusively
                in the state or federal courts located in Philadelphia, Pennsylvania.
              </p>
              <h2>Notices & Communications</h2>
              <p>
                HIPAA LINK may send notices, agreements, disclosures, or other communications electronically via email or
                by posting them on our website. Users are responsible for ensuring their contact information is up to date.
              </p>
              <h2>Government Rights and Compliance</h2>
              <p>
                HIPAA LINK provides the Service for commercial use. Government users acknowledge that the Service is a
                commercial item and agree to use it per commercial license terms.
              </p>
              <h2>Force Majeure</h2>
              <p>
                HIPAA LINK is not liable for delays or failures in performance due to causes beyond its reasonable control,
                including but not limited to natural disasters, acts of war, or failures of third-party service providers.
              </p>
              <h2>Severability</h2>
              <p>
                If any provision of these Terms is found to be invalid, illegal, or unenforceable for any reason, the
                remaining provisions shall continue in full force and effect.
              </p>
              <h2>Survival Clause</h2>
              <p>
                Sections related to liability limitations, indemnity, dispute resolution, and data processing shall
                survive termination of these Terms.
              </p>
              <h2>General Provisions</h2>
              <p>
                These Terms are governed by the laws of Pennsylvania. Users must comply with all applicable laws and
                regulations. Any changes to these Terms will be communicated in advance.
              </p>
              <h2>Contact Information</h2>
              <p>
                For legal notices, support inquiries, or compliance matters, contact HIPAA LINK at: support@hipaalink.net
                or 744 South Street #1081, Philadelphia, PA 19147.
              </p>
            </p>
            </>
          </Typography>
        </Box>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          gap='16px'
          sx={({ breakpoints }) => ({
            [breakpoints.down(768)]: {
              flexDirection: 'column',
              paddingRight: '18px',
            },
            [breakpoints.down(599)]: {
              gridColumnStart: 1,
              gridColumnEnd: 2,
              gridRowStart: 3,
              gridRowEnd: 4,
            },
          })}
        >
          <Stack direction='row'>
            <FormControlLabel
              sx={({ breakpoints }) => ({
                [breakpoints.down(768)]: {
                  marginRight: 0,
                },
              })}
              label={
                <Typography
                  sx={({ breakpoints }) => ({
                    color: '#132F41',
                    fontSize: '17px',
                    fontWeight: 700,
                    lineHeight: '25.5px',
                    textAlign: 'left',
                    [breakpoints.down(768)]: {
                      textAlign: 'justify',
                    },
                  })}
                >
                  I confirm that I have read and accept these terms and conditions.
                </Typography>
              }
              control={
                <Checkbox
                  checked={isChecked}
                  onChange={handleCheckboxChange}
                  icon={<CheckboxIcon />}
                  checkedIcon={<CheckboxCheckedIcon />}
                />
              }
            />
          </Stack>
          <Stack direction='row' alignItems='center' justifyContent='space-between' gap='8px'>
            <Button
              variant='text'
              sx={{
                width: 167,
                height: 44,
                fontWeight: 600,
                fontSize: 17,
                borderRadius: 1,
              }}
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button
              variant='contained'
              disabled={!isChecked}
              sx={{
                width: 167,
                height: 44,
                fontWeight: 600,
                fontSize: 17,
                borderRadius: 1,
                ':disabled': {
                  opacity: '0.25 !important',
                  backgroundColor: '#81CFDD !important',
                },
              }}
              onClick={handleConfirm}
            >
              Accept
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
